import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useSeo, useSite } from "@/hooks/useSite";
import ContentBody from "@/components/site/ContentBody";
import { Chip, PageHero, SitePage } from "@/components/site/SiteBits";

const TABS = [["terms", "/terms", "Terms"], ["privacy", "/privacy", "Privacy"], ["community_guidelines", "/guidelines", "Community guidelines"]];
const SEO = { terms: "Terms", privacy: "Privacy", community_guidelines: "Community guidelines" };

// Highlight the section currently in view.
function useActive(ids) {
  const [active, setActive] = useState(null);
  const key = ids.join("|");
  useEffect(() => {
    const ids = key ? key.split("|") : [];
    if (!ids.length) return undefined;
    const io = new IntersectionObserver((es) => { const v = es.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]; if (v) setActive(v.target.id); }, { rootMargin: "-90px 0px -65% 0px" });
    ids.forEach((id) => { const el = document.getElementById(id); if (el) io.observe(el); });
    return () => io.disconnect();
  }, [key]);
  return active && ids.includes(active) ? active : ids[0];
}

export default function LegalPage({ doc }) {
  const { data, error, loading, reload } = useSite();
  const p = data?.legal_pages?.[doc];
  const face = doc === "privacy" ? data?.privacy_face_data : null; // non-null only while the automatic selfie check is on
  const base = p?.sections || [];
  const sections = face ? [...base, { id: "face-data", number: base.length + 1, title: face.title, body: face.body }] : base;
  const active = useActive(sections.map((s) => s.id));
  useSeo(SEO[doc] || p?.title || "Crushwink", p?.lead);
  return (
    <SitePage loading={loading} error={error} onRetry={reload} testId={`legal-${doc}-page`}>
      <PageHero chip="Legal" title={p?.title} accent={p?.accent} lead={p?.lead} testId="legal-hero" />
      <div className="mt-14 grid items-start gap-10 lg:grid-cols-[240px_minmax(0,1fr)]">
        <nav aria-label="Sections" className="sticky top-24 hidden lg:block" data-testid="legal-toc">
          <ol className="space-y-1 text-sm">
            {sections.map((s) => (
              <li key={s.id}><a href={`#${s.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" }); }}
                className={`s-focus flex gap-3 rounded-xl px-3 py-2 transition-colors ${active === s.id ? "s-chip-choice is-on font-semibold" : "s-muted hover:text-[var(--s-text)]"}`} data-testid={`legal-toc-${s.id}`}>
                <span className="tabular-nums">{s.number}.</span><span className="line-clamp-2">{s.title}</span></a></li>))}
          </ol>
        </nav>
        <article className="s-card min-w-0 p-6 sm:p-10" data-testid="legal-card">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Legal documents">
            {TABS.map(([k, to, l]) => <NavLink key={k} to={to} role="tab" aria-selected={k === doc} className={`s-btn s-focus h-10 px-4 text-sm ${k === doc ? "s-btn-contrast" : "s-btn-ghost"}`} data-testid={`legal-tab-${k}`}>{l}</NavLink>)}
          </div>
          {p?.intro && <div className="s-muted mt-8 leading-7"><ContentBody body={p.intro} testId="legal-intro" /></div>}
          <div className="mt-8 space-y-10">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`legal-section-${s.number}`}>
                <h2 className="flex gap-3 text-xl font-bold tracking-tight"><span className="s-coral tabular-nums">{s.number}.</span>{s.title}</h2>
                {s.body && <div className="s-muted mt-3 leading-7"><ContentBody body={s.body} testId={`legal-section-${s.number}-body`} /></div>}
              </section>))}
          </div>
          {p?.outro && <div className="s-muted mt-10 border-t s-line pt-8 leading-7"><ContentBody body={p.outro} testId="legal-outro" /></div>}
          {!p && <Chip>{doc}</Chip>}
        </article>
      </div>
    </SitePage>
  );
}
